import React, { useEffect, useState } from 'react';
import axios from 'axios';
import NavBar from '../../components/Navbar/NavBar';
import Footer from '../../components/Footer';
import Card from '../../components/Cards/Card';
import SideMenu from '../../components/SideMenu';
import useDocTitle from '../../utils/useDocTitle';
import { useLanguage } from '../../context/LanguageContext';

const Laboratories = () => {
  const { language } = useLanguage(); // Use language context for translations
  useDocTitle(language === 'English' ? 'Laboratories' : 'Laboratorët');

  const [laboratories, setLaboratories] = useState([]);
  const [mainPageData, setMainPageData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch laboratories data
        const laboratoriesResponse = await axios.get('https://annexpilot.com/dashboard/wp-json/wp/v2/laboratories?_embed');
        const labs = laboratoriesResponse.data
          .filter((lab) => lab.page_language[0].name === language) // Filter based on language
          .map((lab) => {
            const featuredMedia = lab.image; // Featured image data
            return {
              id: lab.id,
              title: lab.title.rendered || (language === 'English' ? 'No Title' : 'Nuk ka titull'),
              description: lab.content_description,
              expert: lab.expert_name?.value || (language === 'English' ? 'Unidentified Expert' : 'Ekspert i paidentifikuar'),
              imgSrc: featuredMedia.guid,
              imgAlt: featuredMedia.post_excerpt || (language === 'English' ? 'No alternative text available' : 'Nuk ka tekst alternativ'),
            };
          });
        setLaboratories(labs);

        // Fetch main page data
        const mainPageResponse = await axios.get('https://annexpilot.com/dashboard/wp-json/wp/v2/lab_mainpage');
        const mainData = mainPageResponse.data.find((page) => page.page_language[0].name === language); // Match main page to language
        setMainPageData({
          mainTitle: mainData.main_title,
          mainText: mainData.main_text,
          mainImage: {
            src: mainData.main_image.guid,
            alt: mainData.main_image.post_excerpt,
          },
        });

        // Update menu items dynamically
        setMenuItems([
          { href: '#overview', label: mainData.main_title },
          { href: '#laboratories', label: language === 'English' ? 'Laboratories' : 'Laboratorët' },
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [language]);

  if (loading) {
    return <p>{language === 'English' ? 'Loading...' : 'Duke u ngarkuar...'}</p>;
  }

  return (
    <>
      <NavBar />
      {mainPageData && (
        <div
          className="relative w-full h-96 bg-cover bg-center flex items-center justify-center"
          style={{
            backgroundImage: `url(${mainPageData.mainImage.src})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            height: '550px',
          }}
          role="img"
          aria-label={mainPageData.mainImage.alt || (language === 'English' ? 'Cover image for Laboratories' : 'Imazhi mbulues për Laboratorët')}
        >
          <div className="absolute inset-0 bg-black opacity-60"></div>
          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-5xl font-bold" tabIndex="0">
              {language === 'English' ? 'Laboratories' : 'Laboratorët'}
            </h1>
          </div>
        </div>
      )}

      <div className="flex min-h-screen">
        <aside
          className="w-full md:w-1/4 bg-gray-100 px-4 md:px-8 py-8 md:py-12 sticky top-16 md:h-screen"
          aria-label={language === 'English' ? 'Sidebar navigation for Laboratories' : 'Navigim anësor për Laboratorët'}
        >
          <SideMenu
            backLink="/educational-program"
            backText={language === 'English' ? 'EDUCATIONAL PROGRAMS' : 'PROGRAMET EDUKATIVE'}
            menuItems={menuItems}
          />
        </aside>
        <div className="w-full md:w-3/4 py-8 px-4 md:px-6">
          {mainPageData && (
            <section id="overview" className="container-inner mb-12">
              <h2 className="text-3xl font-semibold mb-6 text-left" tabIndex="0">
                {mainPageData.mainTitle}
              </h2>
              <div
                className="text-lg text-gray-600 leading-relaxed text-left"
                dangerouslySetInnerHTML={{ __html: mainPageData.mainText }}
              ></div>
            </section>
          )}
          <section id="laboratories" className="container-inner mb-12">
            <h2 className="text-3xl font-semibold mb-6 text-left" tabIndex="0">
              {language === 'English' ? 'Laboratories' : 'Laboratorët'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {laboratories.map((lab) => (
                <Card
                  key={lab.id}
                  id={lab.id}
                  title={lab.title}
                  description={lab.description}
                  imgSrc={lab.imgSrc}
                  imgAlt={lab.imgAlt}
                  linkText={language === 'English' ? 'See more' : 'Shiko më shumë'}
                  linkPath={`/resources/laboratories/${lab.id}`}
                  borderColorClass="border-secondary"
                  ariaLabel={
                    language === 'English'
                      ? `Go to laboratory ${lab.title}`
                      : `Shko te laboratori ${lab.title}`
                  }
                />
              ))}
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Laboratories;